import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

import NextJobForm from "../../utils/NextJobForm";
import { showToast } from "../../../../../store/actions";
import { useCronReducer } from "../../../../../util/cron";
import WorkspaceService from "../../../../../service/workspace";
import { ButtonUI, ConfirmPopup } from "../../../../Common";
import { FormTile } from "../../../../Common/Tile";
import ObjectPanel from "../ObjectPanel";
import { checkIfSelectQuery, setPrevState } from "../../../../../util/common";
import CommonLayout from "../../../../Common/Layout";
import DataSource from "../../../../../service/dataSource";
import { regexName } from "../../../../../util/regularExpress";
import { OBJECT_TYPE } from "../../../../../util/Data/DataModeling";
import { DETAUL_CRON_VALUE, JOB_BY_TYPE } from "../../utils/JobData";
import { navScheduledJobs } from "../../../../kmk/AppData";
import HelpContextIcon from "../../../../Common/HelpPanel/HelpContextIcon";
import Scheduler from "../../utils/Scheduler";
import DataPipelineService from "../../../../../service/dataPipelineService";
import ComponentSuspense from "../../../../Common/ComponentSuspense";
import CopyButton from "../../../../Common/CopyButton";
import SmallToolbar from "../../../../Common/SmallToolbar";
import CloneJob from "../../CloneJob";
import UnloadForm from "../../utils/UnloadForm";
const MyAceEditor = React.lazy(() => import("../../../../Common/AceEditor"));

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
  queryArea: {
    width: "100% !important",
    height: "173px !important",
    border: "1px solid",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
  },
}));

const ComputeForm = ({ cluster, setComputeState }) => {
  const clusterList = useSelector((state) => state.clusterList);

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 12,
          sm: 6,
          md: 4,
        }}
      >
        <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
          <InputLabel>Compute Cluster</InputLabel>
          <Select
            value={cluster}
            onChange={(e) => setComputeState(e.target.value)}
            label="Compute Cluster"
            MenuProps={{ sx: { height: "220px" } }}
          >
            {(clusterList || [])?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

const QueryEditor = ({ query, setQuery, workspaceDBName, User_id, cluster, handleError }) => {
  const { classes } = useStyles();
  const queryRef = useRef(null);

  return (
    <Grid sx={{ paddingTop: "2px !important" }} size={12}>
      <Grid size={6}>
        <Typography color={"#00000099"} padding={0} variant="subtitle2" component="span">
          Data Quality Custom SQL (Optional)
        </Typography>
      </Grid>
      <Grid sx={{ paddingTop: "5px !important" }} size={12}>
        <Typography color={"#00000099"} padding={0} variant="caption" component="span" sx={{ mt: 1, mb: 1 }}>
          Use select query only. Query must include HK, SOURCE, ATTRIBUTE, RULE_NAME, CATEGORY, DESCRIPTION and ALERT
          fields.
          <br />
          Copy the sample query
          <CopyButton
            value={
              "select HK, record_source as SOURCE, 'TotalPrice' ATTRIBUTE, 'Custom_TotalPrice' RULE_NAME, 1 as CATEGORY, '' as DESCRIPTION, 'Critical' as Alert\nfrom Model.S_Product \nwhere END_DATE IS NULL AND TotalPrice != Quantity * UnitPrice"
            }
          />
        </Typography>
        <ComponentSuspense>
          <MyAceEditor
            fontSize={14}
            style={{ paddingTop: "10px" }}
            className={classes.queryArea}
            ref={queryRef}
            label="Query"
            value={query}
            setQuery={setQuery}
            location={"SNOWFLAKE"}
            type={"snowflake"}
            runParams={{
              database: workspaceDBName,
              schema: "MODEL",
              runJobAsUserId: User_id,
              cluster,
            }}
            onError={(error) => handleError(error)}
          />
        </ComponentSuspense>
      </Grid>
    </Grid>
  );
};

export default function DataQualityScheduler() {
  const { Workspace_id, Job_id } = useParams();
  const workspace = useSelector((state) => state.workspace);
  const user = useSelector((state) => state.user.currentUser);
  const User_id = user.User_id;

  const config = useSelector((state) => state.config);
  const clusterList = useSelector((state) => state.clusterList);

  const { Name: workspaceName, IsDataVaultEnabled: isDataVaultEnabled, DBName: workspaceDBName } = workspace.workspace;
  const defaultCluster = config.defaultCluster;

  const [initialLoading, setInitialLoading] = useState(true);
  const [qualityJob, setQualityJobState] = useState({
    name: "",
    jobType: "",
    query: "",
    timeZone: "",
    nextRunTime: "",
    loading: false,
    scheduleEnabled: false,
    type: 4,
    DataPipeline_id: "",
    isEntryJob: false,
    queryError: "",
  });

  const [dataPipelineList, setDataPipelineList] = useState([]);
  const [unloadState, setUnloadState] = useState({
    unloadPath: "",
    location: "",
    unloadEnabled: false,
  });

  const [computeState, setComputeState] = useState({
    cluster: defaultCluster,
  });

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobName: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
  });

  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [openClone, setOpenClone] = useState(false);
  const [dataSourcesLoading, setDataSourcesLoading] = useState(false);
  const [dataSources, setDataSources] = useState([]);

  const nextJobIdRef = useRef(nextJobState.nextJobId);
  const errorJobIdRef = useRef(nextJobState.errorJobId);
  const textRef = useRef(null);

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer(DETAUL_CRON_VALUE);

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCancel = () => {
    setPrevState(setDeleteJobState, { openConfirm: false });
  };

  const deleteDataQualityJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteDataQualityJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  const handleSave = async (e, clone = false) => {
    const query = qualityJob.query.trim();
    if (query && !checkIfSelectQuery(query, false)) {
      dispatch(
        showToast({
          message: "Please enter only SELECT query.",
          type: "error",
        })
      );
      setPrevState(setQualityJobState, { loading: false });
      return;
    }

    setPrevState(setQualityJobState, { loading: true });

    const selectedSources = dataSources.filter((source) => source.checked);
    const selectAll = selectedSources.length === dataSources.length;

    const submitData = {
      name: qualityJob.name,
      type: qualityJob.type,
      DataPipeline_id: qualityJob.DataPipeline_id,
      query: query,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: qualityJob.scheduleEnabled || false,
      cluster: computeState.cluster,
      Stage_id: unloadState.location,
      unloadEnabled: unloadState.unloadEnabled || false,
      unloadPath: unloadState.unloadPath || "",
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
          }),
      dataSource: { selectAll, dataSources: selectAll ? [] : selectedSources.map(({ name }) => name) },
      isEntryJob: qualityJob.isEntryJob,
    };

    const wsService = new WorkspaceService();
    const response = Job_id
      ? await wsService.updateDataModelingJob(submitData, Workspace_id, Job_id, 4)
      : await wsService.createDataModelingJob(submitData, Workspace_id, 4);

    if (response.success) {
      if (clone) {
        setPrevState(setQualityJobState, { loading: false });
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setQualityJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  const getDataSources = useCallback(async () => {
    const datasource = new DataSource();

    const { success, data } = await datasource.getObjectNamesByType(
      Workspace_id,
      OBJECT_TYPE.DataSource,
      JOB_BY_TYPE.dataQuality,
      Job_id
    );

    return success ? data : [];
  }, [Job_id, Workspace_id]);

  useEffect(() => {
    const getDataQualitySchedule = async () => {
      const wsService = new WorkspaceService();
      // Some changes
      const response = await wsService.getDataQualitySchedule(Workspace_id, Job_id);
      if (response === "Unauthorized" || !response.success)
        navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

      setPrevState(setQualityJobState, {
        name: response.data.Name,
        jobType: response.data.Detail?.type,
        query: response.data.Detail?.query || "",
        scheduleEnabled: response.data.Enabled,
        timeZone: response.data.ServerTimeZone,
        nextRunTime: response.data.nextRun,
        DataPipeline_id: response.data.DataPipeline_id,
      });

      setPrevState(setUnloadState, {
        unloadPath: response.data.Detail.unloadPath || "",
        unloadEnabled: response.data.Detail.unloadEnabled || false,
        location: response.data.Detail.Stage_id || "",
      });

      setPrevState(setNextJobState, {
        nextDataPipelineId: response.data.NextPipeline_id || "",
        nextJobId: response.data.NextJob_id || nextJobIdRef.current,
        errorDataPipelineId: response.data.ErrorPipeline_id || "",
        errorJobId: response.data.ErrorJob_id || errorJobIdRef.current,
        nextJobToggle: response.data.enableNextJob || false,
      });

      dispatchValues({
        type: "set_values",
        value: response.data.Schedule,
      });

      return response.data;
    };

    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);
      if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
      return [];
    };

    (async () => {
      if (clusterList === false) return;
      setInitialLoading(true);
      setDataSourcesLoading(true);
      setPrevState(setQualityJobState, { loading: true });

      let jobDetails = {};
      if (Job_id) {
        jobDetails = await getDataQualitySchedule();
      }

      let dataPipelines = await getDataPipelineList();
      if (!jobDetails.DataPipeline_id)
        setPrevState(setQualityJobState, {
          DataPipeline_id: dataPipelines?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id,
        });
      setDataPipelineList(dataPipelines);

      setInitialLoading(false);

      //Other details
      const dataSources = await getDataSources();

      if (Job_id && clusterList?.length) {
        //Setting selected cluster
        setPrevState(setComputeState, {
          cluster: clusterList.some((cluster) => cluster.value === jobDetails.Detail.Cluster)
            ? jobDetails.Detail.Cluster
            : defaultCluster,
        });
      }

      //Setting selected refresh data sources
      if (dataSources.length) {
        if (jobDetails?.DataSource?.selectAll === true)
          setDataSources(dataSources.map((item) => ({ ...item, checked: true })));
        else {
          const selectedSources = jobDetails?.DataSource?.dataSources || [];
          setDataSources(dataSources.map((item) => ({ ...item, checked: selectedSources.includes(item.name) })));
        }
      }

      let isEntryJob = false;
      if (Job_id) {
        const currentPipeline = dataPipelines.find(
          ({ DataPipeline_id }) => parseInt(DataPipeline_id) === parseInt(jobDetails.DataPipeline_id)
        );
        isEntryJob = currentPipeline.EntryJob_id === parseInt(Job_id);
      }

      setDataSourcesLoading(false);
      setPrevState(setQualityJobState, { loading: false, isEntryJob });
    })();
  }, [Job_id, Workspace_id, qualityJob.type, navigate, dispatchValues, defaultCluster, getDataSources, clusterList]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`, displayName: "Data Pipelines", active: false },
    { link: "#", displayName: Job_id ? "Edit Data Quality Job" : "Add Data Quality Job", active: true },
  ];

  const disableSave = useMemo(() => {
    return (
      !(!unloadState.unloadEnabled || (unloadState.unloadEnabled && unloadState.location && unloadState.unloadPath)) ||
      qualityJob.name === "" ||
      qualityJob.loading ||
      qualityJob.queryError ||
      computeState.cluster === "" ||
      (nextJobState.nextJobToggle === true && !(nextJobState.nextJobId || nextJobState.errorJobId)) ||
      (isDataVaultEnabled && !(dataSources.some((source) => source.checked) || qualityJob.query.trim()))
    );
  }, [
    unloadState.unloadEnabled,
    unloadState.location,
    unloadState.unloadPath,
    qualityJob.name,
    qualityJob.loading,
    qualityJob.queryError,
    qualityJob.query,
    computeState.cluster,
    nextJobState.nextJobToggle,
    nextJobState.nextJobId,
    nextJobState.errorJobId,
    isDataVaultEnabled,
    dataSources,
  ]);

  /**
   * Alpha Numeric Characters only with Space allow (first letter should Alpha)
   * @param {*} str
   * @returns
   */
  const handleJobName = (str, dataState) => {
    const isValid = regexName(str, "jobName", "_ -");
    if (isValid) setPrevState(setQualityJobState, { [dataState]: str });
  };

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobName: "",
      nextJobId: "",
      errorJobId: "",
      errorSchedule: "",
    });

  const confirmPipelineChange = useCallback((e) => {
    setQualityJobState((prev) => ({
      ...prev,
      DataPipeline_id: e.target.value,
      ...(prev.DataPipeline_id ? { isEntryJob: false } : {}),
    }));
    resetNextJob();
  }, []);

  // Form component to display Add Job Name Field Section
  const addJobForm = () => {
    return (
      <Grid container direction={"row"} spacing={2}>
        <Grid>
          <TextField
            value={qualityJob.name}
            sx={{ width: { xs: "100%", sm: "300px" } }}
            required
            label="Job Name"
            inputProps={{ maxLength: 30 }}
            onChange={(e) => handleJobName(e.target.value, "name")}
          />
        </Grid>
        <Grid>
          <FormControl sx={{ width: { xs: "100%", sm: "300px" } }} required>
            <InputLabel>Pipeline</InputLabel>
            <Select
              value={qualityJob.DataPipeline_id}
              onChange={confirmPipelineChange}
              label="Pipeline"
              disabled={!!Job_id}
              MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
              renderValue={(selected) => {
                const selectedPipeline = dataPipelineList.find((p) => p.DataPipeline_id === selected);
                const isOverflow = (textRef.current?.scrollWidth > textRef.current?.clientWidth || false) && !!Job_id;

                return (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Tooltip placement="bottom" title={selectedPipeline?.Name} arrow disableHoverListener={!isOverflow}>
                      <Typography
                        variant="body1"
                        ref={textRef}
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}
                      >
                        {selectedPipeline?.Name || "Select a pipeline"}
                      </Typography>
                    </Tooltip>
                    {selectedPipeline?.IsDefault === 1 && (
                      <Tooltip placement="right" title="Default Pipeline" arrow>
                        <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                      </Tooltip>
                    )}
                  </Stack>
                );
              }}
            >
              {dataPipelineList.map((p) => (
                <MenuItem
                  key={p.DataPipeline_id}
                  value={p.DataPipeline_id}
                  style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all" }}
                >
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                      {p.Name}
                    </Typography>
                    {p.IsDefault === 1 && (
                      <Tooltip placement="right" title="Default Pipeline" arrow>
                        <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                      </Tooltip>
                    )}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid sx={{ display: "flex", alignItems: "center" }}>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox />}
              value={qualityJob.isEntryJob}
              checked={qualityJob.isEntryJob}
              onChange={() => setQualityJobState((prev) => ({ ...prev, isEntryJob: !prev.isEntryJob }))}
              label="Set as the entry point for the pipeline"
            />
          </FormGroup>
        </Grid>
      </Grid>
    );
  };

  const toggleSchedule = useCallback(
    () => setQualityJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  return (
    <CommonLayout title="Job Scheduler" loading={qualityJob.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          height: "100vh",
          overflowX: "hidden",
          overflowY: "auto",
          paddingBottom: "20px",
        }}
        id="main_box"
      >
        {initialLoading ? (
          <CircularProgress
            color="primary"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ) : (
          <>
            <Backdrop
              sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={qualityJob.loading}
            >
              <CircularProgress color="primary" />
            </Backdrop>
            <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
              <Box className={classes.subContainer}>
                <Box className={classes.PanelContainer}>
                  <Grid container direction="row" sx={{ mb: 4 }}>
                    <Grid sx={{ mb: 1 }} size={12}>
                      {Job_id ? (
                        <Typography className={classes.HeaderTitle} gutterBottom component="div">
                          Edit Data Quality Job
                        </Typography>
                      ) : (
                        <Typography className={classes.HeaderTitle} gutterBottom component="div">
                          Add Data Quality Job
                        </Typography>
                      )}
                      <ButtonUI
                        onClick={handleSave}
                        variant="contained"
                        color="success"
                        disabled={disableSave}
                        sx={{ mb: "5px" }}
                      >
                        Save
                      </ButtonUI>
                      <ButtonUI
                        onClick={handleCancelClick}
                        variant="contained"
                        color="secondary"
                        sx={{ mb: "5px", ml: "15px" }}
                      >
                        Cancel
                      </ButtonUI>

                      {Job_id ? (
                        <>
                          <ButtonUI
                            onClick={() => setOpenClone(true)}
                            variant="contained"
                            color="info"
                            sx={{ mb: "5px", mx: "15px" }}
                            disabled={disableSave}
                          >
                            Clone
                          </ButtonUI>

                          <ButtonUI
                            variant="contained"
                            color="error"
                            onClick={() => {
                              setPrevState(setDeleteJobState, {
                                openConfirm: true,
                                confirmTitle: "Delete Data Quality Job",
                                confirmMessage: `Are you sure you want to delete "${qualityJob.name}" Job?"`,
                              });
                            }}
                            disabled={!Job_id}
                            sx={{ mb: "5px" }}
                          >
                            Delete
                          </ButtonUI>
                        </>
                      ) : (
                        <></>
                      )}

                      <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                        <HelpContextIcon screen="Data Quality Job" />
                      </Box>
                    </Grid>
                    <Grid className={classes.BigTileContainer} size={12}>
                      <FormTile>{addJobForm()}</FormTile>
                    </Grid>

                    {isDataVaultEnabled && (
                      <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                        <ObjectPanel
                          objectType={OBJECT_TYPE.DataSource}
                          objects={dataSources}
                          setObjects={setDataSources}
                          loading={dataSourcesLoading}
                          setLoading={setDataSourcesLoading}
                          getObjects={getDataSources}
                        />
                      </Grid>
                    )}

                    <Grid className={classes.BigTileContainer} size={12}>
                      <SmallToolbar>Schedule</SmallToolbar>
                      <FormTile
                        style={{
                          overflowX: "auto",
                        }}
                      >
                        <Scheduler
                          scheduleEnabled={qualityJob.scheduleEnabled}
                          cronValue={values.cronValue}
                          dispatchValues={dispatchValues}
                          toggleValue={toggleSchedule}
                          timeZone={qualityJob.timeZone}
                          nextRunTime={qualityJob.nextRunTime}
                        />
                      </FormTile>
                    </Grid>

                    <Grid className={classes.BigTileContainer} size={12}>
                      <SmallToolbar>Custom SQL</SmallToolbar>
                      <FormTile
                        style={{
                          overflowX: "auto",
                        }}
                      >
                        <Grid className={classes.BigTileContainer} size={12}>
                          <QueryEditor
                            query={qualityJob.query}
                            setQuery={(value) => setQualityJobState((prev) => ({ ...prev, query: value }))}
                            workspaceDBName={workspaceDBName}
                            User_id={User_id}
                            cluster={computeState.cluster}
                            handleError={(error) => setPrevState(setQualityJobState, { queryError: error })}
                          />
                        </Grid>
                      </FormTile>
                    </Grid>

                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Compute Clusters</SmallToolbar>
                      <FormTile>
                        <ComputeForm
                          cluster={computeState.cluster}
                          setComputeState={(cluster) => {
                            setPrevState(setComputeState, { cluster });
                          }}
                        />
                      </FormTile>
                    </Grid>
                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Error Report</SmallToolbar>
                      <FormTile>
                        <UnloadForm unloadState={unloadState} setUnloadState={setUnloadState} />
                      </FormTile>
                    </Grid>
                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Next Job</SmallToolbar>
                      <FormTile>
                        <NextJobForm
                          dataPipelineList={dataPipelineList}
                          nextJobToggle={nextJobState.nextJobToggle}
                          nextDataPipelineId={nextJobState.nextDataPipelineId}
                          nextJobId={nextJobState.nextJobId}
                          errorDataPipelineId={nextJobState.errorDataPipelineId}
                          errorJobId={nextJobState.errorJobId}
                          setNextJobState={setNextJobState}
                        />
                      </FormTile>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={qualityJob.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.dataQuality}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: deleteJobState.confirmTitle,
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: handleCancel,
            handleClick: deleteDataQualityJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
    </CommonLayout>
  );
}
